import { useState, useEffect, useCallback, useRef } from 'react';

import { getConfig } from '@config';
import { addAction, addError } from '@shared/utils/logger';
import { normalizeWithFallback } from '@shared/utils/normalizeWithFallback';

import type { PlaceInfoEssentials, PlaceInfoPro, PlaceInfoEnterprise, PlaceInfoEnterpriseAtmosphere, PlaceInfoTier, PlaceInfoByTier, PlaceInfoScenario } from './places.types';

// ============================================================================
// Configuration
// ============================================================================

const { locationServiceUri } = getConfig();

// ============================================================================
// Default Values (for normalizeWithFallback)
// ============================================================================

const defaultPlaceInfoEssentials: PlaceInfoEssentials = {
  addressComponents: [{ languageCode: '', types: [], longText: '', shortText: '' }],
  formattedAddress: '',
  id: '',
  location: { latitude: undefined, longitude: undefined },
  name: '',
  types: [],
} as const;

const defaultPlaceInfoPro: PlaceInfoPro = {
  ...defaultPlaceInfoEssentials,
  businessStatus: '',
  displayName: { text: '', languageCode: '' },
} as const;

const defaultPlaceInfoEnterprise: PlaceInfoEnterprise = {
  ...defaultPlaceInfoPro,
  internationalPhoneNumber: '',
  photos: [{ height: 0, width: 0, htmlAttributions: [], name: '', photo_reference: '' }],
  rating: 0,
  userRatingCount: 0,
  websiteUri: '',
} as const;

const defaultPlaceInfoEnterpriseAtmosphere: PlaceInfoEnterpriseAtmosphere = {
  ...defaultPlaceInfoEnterprise,
  editorialSummary: { text: '', languageCode: '' },
} as const;

// ============================================================================
// Field Groups (Google Places API fields by SKU tier)
// ============================================================================

const ESSENTIALS_FIELDS = ['id', 'name', 'formattedAddress', 'location', 'addressComponents', 'types'] as const;
const PRO_FIELDS = [...ESSENTIALS_FIELDS, 'displayName', 'businessStatus'] as const;
const ENTERPRISE_FIELDS = [...PRO_FIELDS, 'websiteUri', 'rating', 'internationalPhoneNumber', 'userRatingCount', 'photos'] as const;
const ENTERPRISE_ATMOSPHERE_FIELDS = [...ENTERPRISE_FIELDS, 'editorialSummary'] as const;

const FIELDS = {
  essentials: ESSENTIALS_FIELDS,
  pro: PRO_FIELDS,
  enterprise: ENTERPRISE_FIELDS,
  enterpriseAtmosphere: ENTERPRISE_ATMOSPHERE_FIELDS,
} as const;

const DEFAULTS = {
  essentials: defaultPlaceInfoEssentials,
  pro: defaultPlaceInfoPro,
  enterprise: defaultPlaceInfoEnterprise,
  enterpriseAtmosphere: defaultPlaceInfoEnterpriseAtmosphere,
} as const;

// ============================================================================
// Core Fetcher (private)
// ============================================================================

type ErrorResult = Readonly<{ code: number; message: string; status: string }>;

const fetchPlaceFromService = async <T extends { error?: ErrorResult }>(
  placeId: string,
  serviceUrl: string,
  fields: string,
  scenario: PlaceInfoScenario,
  language?: string,
): Promise<T | null> => {
  const url = `${serviceUrl}/places/${placeId}?fields=${fields}${language ? `&language=${encodeURIComponent(language)}` : ''}`;
  const response = await fetch(url, {
    headers: { 'x-scenario': scenario },
  });

  if (!response.ok) {
    try {
      return await response.json();
    } catch {
      throw new Error(response.statusText);
    }
  }

  addAction('Places API Call', { context: 'getPlaceInfo', placeId, fields });
  return (await response.json()) as T;
};

const getPlaceInfo = async <T extends PlaceInfoTier>(placeId: string, tier: T, scenario: PlaceInfoScenario, language?: string): Promise<PlaceInfoByTier[T] | null> => {
  const fields = FIELDS[tier].join(',');
  const defaultData = DEFAULTS[tier];

  type PlaceType = PlaceInfoByTier[T] & { error?: ErrorResult };

  try {
    // Fetch from location service (cached)
    const place = await fetchPlaceFromService<PlaceType>(placeId, locationServiceUri, fields, scenario, language);

    if (!place) return null;

    if (place.error) {
      addError(place.error, { placeId, fields, context: 'getPlaceInfo' });
      return null;
    }

    return normalizeWithFallback(place, defaultData, { arrayMerge: 'combine' }) as PlaceInfoByTier[T];
  } catch (error) {
    addError(error, { placeId, fields, context: 'getPlaceInfo' });
    return null;
  }
};

// ============================================================================
// Hook Types
// ============================================================================

type UsePlaceInfoResult<T> = Readonly<{
  isLoading: boolean;
  isCalled: boolean;
  error: Error | null;
  result: T | null;
  fetchPlaceInfo: (placeId: string) => Promise<T | null>;
}>;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for fetching Google Place Info by tier
 *
 * Pricing tiers:
 * - essentials: $5/1K requests - address/location lookups
 * - pro: $17/1K requests - includes displayName
 * - enterprise: $20/1K requests - contact info, ratings, photos
 * - enterpriseAtmosphere: $25/1K requests - editorial summaries, reviews
 *
 * @param tier - The pricing tier determining which fields to fetch
 * @param scenario - The usage context identifier (passed as x-scenario header)
 * @param placeId - Optional place ID to auto-fetch on mount
 */
export const usePlaceInfo = <T extends PlaceInfoTier>(tier: T, scenario: PlaceInfoScenario, placeId?: string | null, language?: string): UsePlaceInfoResult<PlaceInfoByTier[T]> => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCalled, setIsCalled] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<PlaceInfoByTier[T] | null>(null);

  const lastFetchedRef = useRef<string | null>(null);

  const fetchPlaceInfo = useCallback(
    async (id: string): Promise<PlaceInfoByTier[T] | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const place = await getPlaceInfo(id, tier, scenario, language);
        setResult(place);
        setIsCalled(true);
        return place;
      } catch (err) {
        const fetchError = err instanceof Error ? err : new Error('Failed to fetch place info');
        setError(fetchError);
        setIsCalled(true);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [tier, scenario, language],
  );

  useEffect(() => {
    if (!placeId || lastFetchedRef.current === placeId) {
      return;
    }

    lastFetchedRef.current = placeId;
    fetchPlaceInfo(placeId);
  }, [placeId, fetchPlaceInfo]);

  useEffect(() => {
    if (!placeId) {
      setResult(null);
      setError(null);
      setIsCalled(false);
      lastFetchedRef.current = null;
    }
  }, [placeId]);

  return {
    isLoading,
    isCalled,
    error,
    result,
    fetchPlaceInfo,
  };
};
